import React, { useEffect, useRef, useState } from "react"
import {
  Row,
  Col,
  Modal,
  Button,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap"
import {
  Phone,
  User,
  Bot,
  Briefcase,
  Calendar,
  Play,
  MessageCircle,
  ListChecks,
  Volume2,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  AlertCircle,
  Search,
  X,
  PhoneCall,
  FileBarChart2,
  ChevronLeft,
  Clock,
  Inbox,
} from "lucide-react"
import DataTable from "react-data-table-component"
import axios from "axios"
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer"
import { convertISTtoEST } from "ATSComponents/Common/TimeUtils"
import Swal from "sweetalert2"

/* ------------------------------------------------------------------ */
/* Styles (scoped, injected once)                                      */
/* ------------------------------------------------------------------ */
const jsrStyles = `
  @keyframes jsrFadeInUp { from { opacity:0; transform:translateY(10px);} to { opacity:1; transform:translateY(0);} }
  @keyframes jsrFadeIn { from { opacity:0;} to { opacity:1;} }
  @keyframes jsrPulse { 0%,100%{transform:scale(1);} 50%{transform:scale(1.05);} }

  .jsr-modal .modal-dialog { max-width: 1120px; }
  .jsr-modal .modal-content { border:none !important; border-radius:14px !important; overflow:hidden; box-shadow:0 20px 50px rgba(0,0,0,0.18) !important; }
  .jsr-modal .modal-header { display:none; } /* header is custom-built inside the body for full control */
  .jsr-modal .modal-body { padding:0; }

  .jsr-shell { display:flex; flex-direction:column; max-height:85vh; height:85vh; animation: jsrFadeIn .15s ease; }

  /* ---- hero / header (shared by list + detail views) ---- */
  .jsr-hero {
    background: linear-gradient(120deg,#4338ca 0%,#6d28d9 48%,#0ea5e9 100%);
    color:#fff; padding:18px 22px 16px; position:relative; overflow:hidden; flex-shrink:0;
  }
  .jsr-hero::before {
    content:""; position:absolute; left:-30px; bottom:-70px; width:220px; height:220px;
    background:radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%); pointer-events:none;
  }
  .jsr-hero::after {
    content:""; position:absolute; right:-50px; top:-70px; width:220px; height:220px;
    background:radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%); pointer-events:none;
  }
  .jsr-hero-row { display:flex; align-items:flex-start; gap:12px; position:relative; z-index:1; }
  .jsr-back-btn {
    display:flex; align-items:center; justify-content:center; width:32px; height:32px; flex-shrink:0;
    border:none; border-radius:10px; background:rgba(255,255,255,0.16); color:#fff; cursor:pointer;
    transition: background .15s ease; margin-top:1px;
  }
  .jsr-back-btn:hover { background:rgba(255,255,255,0.3); }
  .jsr-hero-icon {
    width:36px; height:36px; border-radius:11px; background:rgba(255,255,255,0.18);
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.25);
  }
  .jsr-hero-title { font-size:17.5px; font-weight:800; line-height:1.25; letter-spacing:-0.2px; }
  .jsr-hero-sub { font-size:12px; color:rgba(255,255,255,0.82); margin-top:4px; }
  .jsr-hero-close {
    position:absolute; top:16px; right:18px; background:rgba(255,255,255,0.16); z-index:2;
    border:none; border-radius:50%; width:30px; height:30px; display:flex;
    align-items:center; justify-content:center; color:#fff; cursor:pointer; transition: background .15s ease;
  }
  .jsr-hero-close:hover { background:rgba(255,255,255,0.32); }

  /* ---- tabs: segmented pill switcher ---- */
  .jsr-tabs {
    display:inline-flex; gap:3px; padding:4px; margin-top:16px; position:relative; z-index:1;
    background:rgba(255,255,255,0.14); border-radius:999px;
  }
  .jsr-tab-btn {
    display:inline-flex; align-items:center; gap:6px; padding:7px 16px; border:none;
    background:transparent; color:rgba(255,255,255,0.82); font-size:12.5px; font-weight:700;
    border-radius:999px; cursor:pointer; transition: all .18s ease;
  }
  .jsr-tab-btn:hover { color:#fff; }
  .jsr-tab-btn.active { background:#fff; color:#4338ca; box-shadow:0 2px 8px rgba(0,0,0,0.18); }
  .jsr-tab-count {
    display:inline-flex; align-items:center; justify-content:center; min-width:19px; height:18px;
    padding:0 6px; border-radius:999px; font-size:10.5px; font-weight:800;
    background:rgba(255,255,255,0.28); color:#fff;
  }
  .jsr-tab-btn.active .jsr-tab-count { background:#ede9fe; color:#4338ca; }

  /* ---- filter panel (list view): everything on a single row ---- */
  .jsr-filter-panel { background:#f8fafc; border-bottom:1px solid #e2e8f0; padding:11px 20px; flex-shrink:0; }
  .jsr-filter-bar { display:flex; align-items:flex-end; flex-wrap:nowrap; gap:10px; }
  .jsr-filter-group { display:flex; flex-direction:column; gap:3px; flex:0 1 auto; min-width:0; }
  .jsr-filter-group label { font-size:10px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:0.03em; white-space:nowrap; }
  .jsr-filter-group .form-select, .jsr-filter-group .form-control { height:32px; font-size:12.5px; }
  .jsr-filter-search-wrap { position:relative; flex:1 1 150px; min-width:130px; }
  .jsr-reset-btn { height:32px; padding:0 14px; font-weight:600; font-size:12.5px; flex:0 0 auto; }
  .jsr-date-error { margin-top:6px; font-size:11px; color:#dc3545; font-weight:600; }
  .jsr-note-chip {
    display:inline-flex; align-items:center; gap:4px; font-weight:600; font-size:11px;
    padding:6px 10px; border-radius:999px; white-space:nowrap; flex:0 0 auto; height:32px; box-sizing:border-box;
  }
  .jsr-notes-inline { display:flex; align-items:center; gap:8px; flex:0 0 auto; }

  /* ---- scrollable content area (shared) ---- */
  .jsr-content-scroll { flex:1 1 auto; overflow-y:auto; min-height:0; display:flex; flex-direction:column; }
  .jsr-table-wrap { padding: 12px 20px 18px; }

  /* ---- table borders (react-data-table-component) ---- */
  #JSR_Table .rdt_Table { border:1px solid #e2e8f0; border-radius:8px; overflow:hidden; }
  #JSR_Table .rdt_TableHead { border-bottom:2px solid #dbe4ee; }
  #JSR_Table .rdt_TableHeadRow { border-bottom:1px solid #dbe4ee; background:#f8fafc; min-height:42px; }
  #JSR_Table .rdt_TableRow { border-bottom:1px solid #eef1f5; }
  #JSR_Table .rdt_TableCol { padding: 8px 10px !important; border-right:1px solid #eef1f5; }
  #JSR_Table .rdt_TableCol:last-child { border-right:none; }
  #JSR_Table .rdt_TableCell { padding: 6px 10px !important; border-right:1px solid #f1f4f7; }
  #JSR_Table .rdt_TableCell:last-child { border-right:none; }
  #JSR_Table .jsr-col-title { font-size:12px; font-weight:700; white-space:nowrap; }

  /* ---- detail views (evaluation / talent scan / attempt history) ---- */
  .jsr-detail-body { padding:18px 20px 20px; flex:1 1 auto; display:flex; flex-direction:column; min-height:0; }
  .jsr-screening-split-body { display:flex; align-items:stretch; gap:0; flex:1 1 auto; min-height:0; }
  .jsr-screening-details-pane { flex:1 1 58%; max-width:58%; overflow-y:auto; padding:24px 14px 28px 24px; }
  .jsr-screening-transcript-pane {
    flex:1 1 42%; max-width:42%; border-left:1px solid #eef0f3;
    background:linear-gradient(180deg,#f9fafb,#f3f4f6); display:flex; flex-direction:column;
    padding:24px 20px 24px 14px; min-height:0;
  }
  .jsr-info-card { transition: all 0.2s ease; }
  .jsr-info-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
  .jsr-question-item { transition: all 0.2s ease; }
  .jsr-question-item:hover { transform: translateX(3px); box-shadow: 0 2px 8px rgba(124,58,237,0.12); }

  /* ---- talent scan result cards (basic, kept for compatibility) ---- */
  .jsr-ts-card { background:#fff; border-radius:10px; border:1px solid #e2e8f0; padding:14px 16px; }
  .jsr-ts-metrics { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:12px; }
  .jsr-ts-metrics > div { border-radius:8px; padding:8px 10px; text-align:center; }
  .jsr-ts-table th, .jsr-ts-table td { padding:6px 8px !important; font-size:12px; }

  /* ---- talent scan DETAILED result cards (matches Telephonic Tests page UI) ---- */
  .jsr-tsr-shell { background:#fff; border-radius:12px; border:1px solid #e2e8f0; padding:16px 18px; margin-bottom:14px; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
  .jsr-tsr-header {
    display:flex; align-items:flex-start; justify-content:space-between; gap:12px;
    background:linear-gradient(135deg,#0c447c,#1a5a99); color:#fff;
    margin:-16px -18px 14px; padding:14px 18px; border-radius:12px 12px 0 0;
  }
  .jsr-tsr-title { font-size:15px; font-weight:800; }
  .jsr-tsr-subtitle { font-size:12px; opacity:0.85; margin-top:2px; }
  .jsr-tsr-badge { display:inline-flex; align-items:center; gap:4px; padding:4px 12px; border-radius:999px; font-size:11px; font-weight:700; }
  .jsr-tsr-badge.success { background:#dcfce7; color:#15803d; }
  .jsr-tsr-badge.danger { background:#fee2e2; color:#b91c1c; }
  .jsr-tsr-badge.neutral { background:#f1f5f9; color:#475467; }

  .jsr-tsr-metrics { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:14px; }
  .jsr-tsr-metric { border-radius:10px; padding:12px 10px; text-align:center; border:1px solid #e2e8f0; }
  .jsr-tsr-metric.info { background:#eff6ff; border-color:#bfdbfe; }
  .jsr-tsr-metric.good { background:#ecfdf5; border-color:#a7f3d0; }
  .jsr-tsr-metric.mid { background:#fffbeb; border-color:#fde68a; }
  .jsr-tsr-metric.low { background:#fef2f2; border-color:#fecaca; }
  .jsr-tsr-metric.neutral { background:#f8fafc; border-color:#e2e8f0; }
  .jsr-tsr-metric-val { font-size:17px; font-weight:800; color:#1f2937; }
  .jsr-tsr-metric.info .jsr-tsr-metric-val { color:#1d4ed8; }
  .jsr-tsr-metric.good .jsr-tsr-metric-val { color:#059669; }
  .jsr-tsr-metric.mid .jsr-tsr-metric-val { color:#d97706; }
  .jsr-tsr-metric.low .jsr-tsr-metric-val { color:#dc2626; }
  .jsr-tsr-metric-label { font-size:10px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:0.4px; margin-top:3px; }

  .jsr-tsr-info-card { background:#f8fafc; border-radius:10px; border:1px solid #e2e8f0; padding:12px 14px; height:100%; }
  .jsr-tsr-info-heading { font-size:11px; font-weight:800; color:#475467; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:8px; }
  .jsr-tsr-kv-row { display:flex; justify-content:space-between; gap:10px; padding:6px 0; border-bottom:1px dashed #e2e8f0; font-size:12.5px; }
  .jsr-tsr-kv-row:last-child { border-bottom:none; }
  .jsr-tsr-kv-key { color:#64748b; font-weight:600; }
  .jsr-tsr-kv-val { color:#1f2937; font-weight:700; text-align:right; }

  .jsr-tsr-score-pill { display:inline-block; padding:2px 8px; border-radius:999px; font-weight:700; font-size:11.5px; }
  .jsr-tsr-score-pill.good { background:#dcfce7; color:#15803d; }
  .jsr-tsr-score-pill.mid { background:#fef3c7; color:#92400e; }
  .jsr-tsr-score-pill.low { background:#fee2e2; color:#b91c1c; }

  .jsr-empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding: 48px 20px; color:#94a3b8; }

  /* ---- table empty state (no records found) ---- */
  .jsr-table-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:46px 20px; gap:14px; }
  .jsr-table-empty-icon {
    width:64px; height:64px; border-radius:50%; background:linear-gradient(135deg,#eef2ff,#e0e7ff);
    display:flex; align-items:center; justify-content:center; color:#5b7bb8;
  }
  .jsr-table-empty-title { font-size:15px; font-weight:700; color:#334155; }
  .jsr-table-empty-sub { font-size:12.5px; color:#94a3b8; margin-top:-8px; }
`

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`
}

const formatDateTime = dateStr => {
  if (!dateStr || dateStr === "-") return "-"
  const normalized = dateStr.replace(" ", "T")
  const d = new Date(normalized)
  if (isNaN(d.getTime())) return dateStr
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()
  let hours = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, "0")
  const ampm = hours >= 12 ? "PM" : "AM"
  return `${day}/${month}/${year}, ${String(hours).padStart(
    2,
    "0"
  )}:${minutes} ${ampm}`
}

const capitalizeFirst = str =>
  !str || str === "-" ? str : String(str).replace(/\b\w/g, c => c.toUpperCase())
const textValue = v => (v === null || v === undefined || v === "" ? "-" : v)
const asNumber = v => (Number.isFinite(Number(v)) ? Number(v) : 0)
const formatScore = v =>
  Number.isInteger(asNumber(v)) ? String(asNumber(v)) : asNumber(v).toFixed(2)
const formatPct = v => formatScore(v)

const formatTalentScanDate = value => {
  if (!value) return "-"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const getLatestTalentScanHistory = history => {
  if (!Array.isArray(history) || !history.length) return null
  const withData = history.filter(h => h?.response_data)
  if (!withData.length) return null
  return [...withData].sort((a, b) => {
    const aTime = new Date(
      a.completed_at || a.scheduled_at || a.sent_at || 0
    ).getTime()
    const bTime = new Date(
      b.completed_at || b.scheduled_at || b.sent_at || 0
    ).getTime()
    return bTime - aTime
  })[0]
}

const getScoreRecordDate = record =>
  record?.completed_at ||
  record?.scheduled_at ||
  record?.sent_at ||
  record?.missed_at ||
  null

const getLatestScoreRecord = records => {
  if (!Array.isArray(records) || !records.length) return null
  return [...records].sort(
    (a, b) =>
      new Date(getScoreRecordDate(b) || 0) -
      new Date(getScoreRecordDate(a) || 0)
  )[0]
}

const getLatestScoredRecord = records => {
  if (!Array.isArray(records) || !records.length) return null
  return [...records]
    .filter(r => r?.score !== null && r?.score !== undefined)
    .sort(
      (a, b) =>
        new Date(getScoreRecordDate(b) || 0) -
        new Date(getScoreRecordDate(a) || 0)
    )[0]
}

const getScoreRecordStatus = record => {
  if (!record) return "-"
  if (record.completed_at) return "Completed"
  if (record.missed_at) return "Missed"
  if (record.scheduled_at) return "Scheduled"
  if (record.sent_at) return "Sent"
  return capitalizeFirst(record.event_type || "-")
}

const getCandidateName = c => {
  const full = [c?.first_name, c?.middle_name, c?.last_name]
    .filter(Boolean)
    .join(" ")
  return full || c?.name || c?.candidate_name || c?.display_name || "-"
}
const getCandidateEmail = c =>
  c?.email || c?.candidate_email || c?.display_email || "-"
const getCandidatePhone = c =>
  c?.mobile_phone || c?.phone || c?.candidate_phone || c?.display_phone || "-"

const SCREENING_STATUS_OPTIONS = [
  { value: "Completed", label: "Completed" },
  { value: "Call_attempted", label: "Call Attempted" },
  { value: "Initiated", label: "Initiated" },
  { value: "Call_back", label: "Call Back" },
  { value: "Not_answered", label: "Not Answered" },
  { value: "Rejected", label: "Rejected" },
]

const paginationComponentOptions = {
  selectAllRowsItem: true,
  selectAllRowsItemText: "All",
}
const CustomLoader = () => (
  <OrgTableShimmer columns={6} rows={6} titleWidth="200px" />
)

const TableEmptyState = ({ icon, title, subtitle }) => (
  <div className="jsr-table-empty">
    <div className="jsr-table-empty-icon">{icon}</div>
    <div>
      <div className="jsr-table-empty-title">{title}</div>
      {subtitle && <div className="jsr-table-empty-sub">{subtitle}</div>}
    </div>
  </div>
)

const avatarColors = [
  "#0d6efd",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
]
const colorForName = name => {
  if (!name) return avatarColors[0]
  let hash = 0
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return avatarColors[Math.abs(hash) % avatarColors.length]
}
const initials = name => {
  if (!name || name === "-") return "?"
  return name
    .trim()
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

const AvatarCell = ({ name, email, isLink, onClick }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      height: 42,
      padding: "4px 0",
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: "50%",
        background: colorForName(name),
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials(name)}
    </div>
    <div style={{ lineHeight: 1.3, minWidth: 0, flex: 1 }}>
      {isLink ? (
        <span
          title={name || "-"}
          style={{
            color: "#0d6efd",
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          }}
          onClick={onClick}
        >
          {name || "-"}
        </span>
      ) : (
        <div
          title={name || "-"}
          style={{
            fontWeight: 600,
            color: "#1f2937",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name || "-"}
        </div>
      )}
      <div
        title={email || "-"}
        style={{
          fontSize: 11.5,
          color: "#6b7280",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {email || "-"}
      </div>
    </div>
  </div>
)

/* ==================================================================== */
/* MAIN COMPONENT                                                       */
/* ==================================================================== */
const JobScreeningResultsModal = ({ show, onClose, job, companyId }) => {
  const jobId = job?.id ?? job?.job_id ?? job?.job_order_id ?? null
  const jobTitle = job?.job_title || job?.position || "Job"

  /* `view` drives which single screen is rendered inside the one Modal:
     'list' (candidate table) | 'evaluation' | 'talentScan' | 'scoreDetails' */
  const [view, setView] = useState("list")

  const [activeTab, setActiveTab] = useState("telephonic")
  const [searchTerm, setSearchTerm] = useState("")
  const [fromDate, setFromDate] = useState(todayStr())
  const [toDate, setToDate] = useState(todayStr())
  const [screeningStatus, setScreeningStatus] = useState("")
  const [dateError, setDateError] = useState("")
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

  /* ---- telephonic tab state ---- */
  const [records, setRecords] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const [resultData, setResultData] = useState(null)
  const [resultType, setResultType] = useState("")
  const [resultLoading, setResultLoading] = useState(false)
  const [resultError, setResultError] = useState("")

  /* ---- talent scan tab state ---- */
  const [talentScanRecords, setTalentScanRecords] = useState([])
  const [talentScanTotalRows, setTalentScanTotalRows] = useState(0)
  const [talentScanPerPage, setTalentScanPerPage] = useState(10)
  const [talentScanLoading, setTalentScanLoading] = useState(false)
  const [talentScanCandidate, setTalentScanCandidate] = useState(null)
  const [talentScanResults, setTalentScanResults] = useState([])
  const [talentScanResultLoading, setTalentScanResultLoading] = useState(false)
  const [viewingTestResultMap, setViewingTestResultMap] = useState({})
  const [loadingResultMap, setLoadingResultMap] = useState({})
  const [scoreDetailsCandidate, setScoreDetailsCandidate] = useState(null)
  const screeningCutOff = talentScanRecords?.[0]?.screening_percentage ?? null

  const initializedRef = useRef(false)

  /* Reset + initial load whenever modal opens for a (possibly new) job */
  useEffect(() => {
    if (!show || !jobId) return
    setView("list")
    setActiveTab("telephonic")
    setSearchTerm("")
    setFromDate(todayStr())
    setToDate(todayStr())
    setScreeningStatus("")
    setDateError("")
    setPerPage(10)
    setTalentScanPerPage(10)
    setTalentScanRecords([])
    setTalentScanTotalRows(0)
    setResetPaginationToggle(prev => !prev)
    initializedRef.current = true
    fetchCallRecords(1, 10, {
      jobId,
      search: "",
      fromDate: todayStr(),
      toDate: todayStr(),
      screeningStatus: "",
    })
  }, [show, jobId])

  const getCandidateTalentScanId = candidate =>
    candidate?.ts_candidate_id ??
    candidate?.talent_scan_id ??
    candidate?.talent_scan_candidate_id ??
    candidate?.talent_scan_candidate?.id
  const getCandidateJobId = candidate =>
    candidate?.job_id ??
    candidate?.job?.job_id ??
    candidate?.job?.id ??
    (jobId ? Number(jobId) : undefined)

  /* -------------------- API: telephonic call records -------------------- */
  const fetchCallRecords = async (page = 1, size = perPage, overrides = {}) => {
    const jId = overrides.jobId !== undefined ? overrides.jobId : jobId
    const search =
      overrides.search !== undefined ? overrides.search : searchTerm
    const fd = overrides.fromDate !== undefined ? overrides.fromDate : fromDate
    const td = overrides.toDate !== undefined ? overrides.toDate : toDate
    const status =
      overrides.screeningStatus !== undefined
        ? overrides.screeningStatus
        : screeningStatus

    if (!jId) return
    if (fd && td && new Date(fd) > new Date(td)) {
      setDateError("From date cannot be after To date.")
      return
    }
    setDateError("")
    setLoading(true)
    try {
      const params = { page, size, job_id: jId }
      if (companyId) params.company_id = companyId
      if (search?.trim()) params.search = search.trim()
      if (fd) params.from_date = fd
      if (td) params.to_date = td
      if (status) params.screening_status = status
      const { data } = await axios.get("/api/v1/call-records/", { params })
      const apiData = data?.data || []
      const formatted = apiData.map(item => {
        const primary = item.primary_evaluation || {}
        const secondary = item.secondary_evaluation || {}
        return {
          ...item,
          evaluation_id: primary?.id || secondary?.id,
          interview_round: primary?.id
            ? "primary"
            : secondary?.id
            ? "secondary"
            : "-",
          call_status: primary?.call_status || secondary?.call_status || "-",
        }
      })
      setRecords(formatted)
      setTotalRows(data?.count || 0)
    } catch (error) {
      console.error("Error fetching call records:", error)
      setRecords([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }

  /* -------------------- API: talent scan candidate scores -------------------- */
  const fetchTalentScanCandidates = async (
    page = 1,
    size = talentScanPerPage,
    overrides = {}
  ) => {
    const jId = overrides.jobId !== undefined ? overrides.jobId : jobId
    const search =
      overrides.search !== undefined ? overrides.search : searchTerm

    if (!jId) {
      setTalentScanRecords([])
      setTalentScanTotalRows(0)
      return
    }

    setTalentScanLoading(true)
    try {
      const params = { job_id: jId, page, size }
      if (companyId) params.company_id = companyId
      if (search?.trim()) params.search = search.trim()

      const { data } = await axios.get("/api/v1/candidate-scores/", { params })
      const candidates = Array.isArray(data?.results) ? data.results : []
      const total = data?.count ?? candidates.length

      const normalized = candidates.map((c, idx) => {
        const scoreRecords = Array.isArray(c.score_records)
          ? c.score_records
          : []
        const latestRecord = getLatestScoreRecord(scoreRecords)
        const latestScoredRecord = getLatestScoredRecord(scoreRecords)
        const mappedScore =
          latestScoredRecord?.score ??
          c.secondary_score ??
          c.telephonic_score ??
          null
        const id =
          c.job_candidate_id ??
          c.candidate_job_id ??
          c.candidate_id ??
          c.id ??
          idx + 1

        const latestTsHistory = getLatestTalentScanHistory(
          c.talent_scan_history
        )
        const rawResponseData = latestTsHistory?.response_data || null
        const tsData = Array.isArray(rawResponseData?.results)
          ? rawResponseData.results[0] || null
          : rawResponseData
        const tsAtsDetails = tsData?.ats_details || {}

        return {
          ...c,
          job_candidate_id: id,
          candidate_id: c.candidate_id ?? c.ts_candidate_id ?? c.id ?? idx + 1,
          job_id: c.job_id ?? c.job?.job_id ?? c.job?.id ?? Number(jId),
          talent_scan_id:
            c.ts_candidate_id ??
            c.talent_scan_candidate_id ??
            c.talent_scan_id ??
            c.talent_scan_candidate?.id ??
            null,
          display_name: getCandidateName(c),
          display_email: getCandidateEmail(c),
          display_phone: getCandidatePhone(c),
          secondary_score: mappedScore,
          score_records: scoreRecords,
          latest_score_record: latestRecord,
          latest_score_status: getScoreRecordStatus(latestRecord),
          latest_score_date: getScoreRecordDate(latestRecord),
          ts_total_secured:
            tsAtsDetails.total_score_secured ??
            tsData?.total_candidate_score ??
            null,
          ts_total_score:
            tsAtsDetails.actual_total_score ?? tsData?.total_test_score ?? null,
          ts_percentage:
            tsAtsDetails.percentage_score ??
            tsData?.candidate_percentage_score ??
            null,
          ts_qualified: tsAtsDetails.is_test_qualified ?? null,
        }
      })

      setTalentScanRecords(normalized)
      setTalentScanTotalRows(total)
    } catch (error) {
      console.error("Error fetching Talent Scan candidate scores:", error)
      setTalentScanRecords([])
      setTalentScanTotalRows(0)
    } finally {
      setTalentScanLoading(false)
    }
  }

  /* Filter handlers */
  const handleScreeningStatusChange = val => {
    setScreeningStatus(val)
    if (activeTab === "telephonic")
      fetchCallRecords(1, perPage, { screeningStatus: val })
  }

  const handleFromDateChange = val => {
    setFromDate(val)
    if (val && toDate && new Date(val) > new Date(toDate)) {
      setDateError("From date cannot be after To date.")
      return
    }
    setDateError("")
    setResetPaginationToggle(prev => !prev)
    if (activeTab === "telephonic")
      fetchCallRecords(1, perPage, { fromDate: val })
  }

  const handleToDateChange = val => {
    setToDate(val)
    if (fromDate && val && new Date(fromDate) > new Date(val)) {
      setDateError("From date cannot be after To date.")
      return
    }
    setDateError("")
    setResetPaginationToggle(prev => !prev)
    if (activeTab === "telephonic")
      fetchCallRecords(1, perPage, { toDate: val })
  }

  const handleSearchChange = val => setSearchTerm(val)

  useEffect(() => {
    if (!show || !jobId) return
    const t = setTimeout(() => {
      if (activeTab === "telephonic")
        fetchCallRecords(1, perPage, { search: searchTerm })
      else
        fetchTalentScanCandidates(1, talentScanPerPage, { search: searchTerm })
    }, 400)
    return () => clearTimeout(t)
  }, [searchTerm])

  const handleResetFilters = () => {
    setSearchTerm("")
    setFromDate(todayStr())
    setToDate(todayStr())
    setScreeningStatus("")
    setDateError("")
    setResetPaginationToggle(prev => !prev)
    if (activeTab === "telephonic") {
      fetchCallRecords(1, perPage, {
        search: "",
        fromDate: todayStr(),
        toDate: todayStr(),
        screeningStatus: "",
      })
    } else {
      fetchTalentScanCandidates(1, talentScanPerPage, { search: "" })
    }
  }

  const handleTabChange = tab => {
    if (tab === activeTab) return
    setActiveTab(tab)
    setSearchTerm("")
    setResetPaginationToggle(prev => !prev)
    if (tab === "telephonic") {
      fetchCallRecords(1, perPage, { search: "" })
    } else {
      fetchTalentScanCandidates(1, talentScanPerPage, { search: "" })
    }
  }

  const handlePageChange = page => fetchCallRecords(page, perPage)
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage)
    fetchCallRecords(page, newPerPage)
  }
  const handleTalentScanPageChange = page =>
    fetchTalentScanCandidates(page, talentScanPerPage)
  const handleTalentScanPerRowsChange = (newPerPage, page) => {
    setTalentScanPerPage(newPerPage)
    fetchTalentScanCandidates(page, newPerPage)
  }

  /* Return to the candidate list from any detail view (does not close the modal) */
  const backToList = () => setView("list")

  /* -------------------- evaluation detail fetchers -------------------- */
  const handleViewFirstScreening = async row => {
    const id = row.primary_evaluation?.external_call_id
    if (!id) return
    setResultType("First Screening")
    setResultData(null)
    setResultError("")
    setView("evaluation")
    setResultLoading(true)
    try {
      const { data } = await axios.get(
        `/api/v1/get-primary-evaluation/?id=${id}`
      )
      const evalData = data?.data || {}
      const transcript = data?.transcript || []
      const recording = data?.recording || ""
      const screeningQuestions = Object.entries(evalData)
        .filter(([key]) => /^\d+$/.test(key))
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, value]) => value)

      setResultData({
        candidate_name: evalData["Candidate Name"] || row.candidate_name || "-",
        candidate_phone: evalData["Candidate Phone"] || row.phone || "-",
        agent_name: evalData["Agent Name"] || "-",
        job_title: row.job_title || jobTitle || "-",
        designation: row.designation || "-",
        date_of_interview: evalData.date_of_interview || "-",
        next_interview:
          evalData["Date And Time For Next Interview"] ||
          evalData["Next Interview Date Time"] ||
          "-",
        questions: screeningQuestions,
        transcript,
        recording,
      })
    } catch (error) {
      console.error("Error fetching primary evaluation:", error)
      setResultError(
        error?.response?.data?.message || "Failed to fetch evaluation data."
      )
      setResultData(null)
    } finally {
      setResultLoading(false)
    }
  }

  const handleViewSecondScreening = async row => {
    const id = row.secondary_evaluation?.external_call_id
    if (!id) return
    setResultType("Screening")
    setResultData(null)
    setResultError("")
    setView("evaluation")
    setResultLoading(true)
    try {
      const { data } = await axios.get(
        `/api/v1/get-secondary-evaluation/?id=${id}`
      )
      const evalData = data?.data || {}
      const transcript = data?.transcript || []
      const recording = data?.recording || ""

      setResultData({
        candidate_name:
          evalData["Candidate Name"] ||
          evalData.candidate_name ||
          row.candidate_name ||
          "-",
        candidate_phone: evalData["Candidate Phone"] || row.phone || "-",
        agent_name: evalData["Agent Name"] || evalData.agent_name || "-",
        job_title: evalData.job_position || row.job_title || jobTitle || "-",
        designation: row.designation || "-",
        date_of_interview: evalData.date_of_interview || "-",
        next_interview:
          evalData["Next Interview Date Time"] ||
          evalData.date_of_interview ||
          "-",
        questions: evalData["Questions"] || [],
        transcript,
        recording,
        sections: evalData.sections || null,
        overall_score: evalData.overall_score ?? null,
        key_strengths: evalData.key_strengths || "",
        overall_recommendation: evalData.overall_recommendation || "",
        priority_improvements: evalData.priority_improvements || "",
      })
    } catch (error) {
      console.error("Error fetching secondary evaluation:", error)
      setResultError(
        error?.response?.data?.message || "Failed to fetch evaluation data."
      )
      setResultData(null)
    } finally {
      setResultLoading(false)
    }
  }

  const handleOpenTalentScanResults = async candidate => {
    const talentScanId = getCandidateTalentScanId(candidate)
    const jId = getCandidateJobId(candidate)
    if (!talentScanId || !jId) return

    const rowKey = candidate.job_candidate_id ?? candidate.candidate_id
    setLoadingResultMap(cur => ({ ...cur, [rowKey]: true }))

    try {
      const response = await axios.post(
        "/api/v1/candidate-talent-scan-result/",
        { talent_scan_id: talentScanId, job_id: jId },
        { params: { page: 1, size: 10, company_id: companyId } }
      )
      const results = response?.data?.talent_scan_result?.results || []

      if (!results.length) {
        Swal.fire({
          icon: "info",
          title: "No Test Attempted",
          text: "This candidate has not attempted any Talent Scan test yet.",
          confirmButtonColor: "#0c447c",
        })
        return
      }

      setTalentScanCandidate(candidate)
      setTalentScanResults(results)
      setView("talentScan")
    } catch (error) {
      console.error("Failed to fetch candidate talent scan result", error)
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: "Failed to fetch Talent Scan result. Please try again.",
        confirmButtonColor: "#0c447c",
      })
    } finally {
      setLoadingResultMap(cur => ({ ...cur, [rowKey]: false }))
    }
  }

  const handleViewDetailedTalentResult = async test => {
    if (test?.id == null || test?.attempt_id == null) return
    const key = `${test.id}-${test.attempt_id}`
    setViewingTestResultMap(cur => ({ ...cur, [key]: true }))
    try {
      const response = await axios.post(
        "/api/v1/ts-view-test/create-dashboard-link/",
        {
          assign_test_id: test.id,
          attempt_id: test.attempt_id,
        }
      )
      if (response?.data?.url) window.open(response.data.url, "_blank")
    } catch (error) {
      console.error("Failed to generate Talent Scan detail link", error)
    } finally {
      setViewingTestResultMap(cur => ({ ...cur, [key]: false }))
    }
  }

  const openScoreDetails = row => {
    setScoreDetailsCandidate(row)
    setView("scoreDetails")
  }

  /* -------------------- columns -------------------- */
  const columns = [
    {
      name: <span className="jsr-col-title">Recruiter</span>,
      selector: row =>
        row.secondary_evaluation?.hr_name ||
        "-",
      sortable: true,
      minWidth: "150px",
      cell: row => {
          const name =
            row.primary_evaluation?.hr_name ||
            row.secondary_evaluation?.hr_name ||
            "-";

          const email =
            row.primary_evaluation?.hr_email ||
            row.secondary_evaluation?.hr_email ||
            "-";

          return (
            <AvatarCell
              name={name}
              email={email}
            />
          );
        }
      },
    {
      name: <span className="jsr-col-title">Candidate</span>,
      selector: row => row.candidate_name || "-",
      sortable: true,
      minWidth: "170px",
      cell: row => (
        <AvatarCell
          name={row.candidate_name}
          email={row.candidate_email}
          isLink
          onClick={() => {
            if (row.primary_evaluation?.id) handleViewFirstScreening(row)
            else if (row.secondary_evaluation?.id)
              handleViewSecondScreening(row)
          }}
        />
      ),
    },
    {
      name: <span className="jsr-col-title">Phone</span>,
      selector: row => row.phone || "-",
      sortable: true,
      width: "115px",
      cell: row => <span title={row.phone || "-"}>{row.phone || "-"}</span>,
    },
    {
      name: <span className="jsr-col-title">Status</span>,
      width: "100px",
      center: true,
      cell: row => {
        const status = row.secondary_evaluation?.call_status
        return (
          <span
            title={status || "-"}
            style={{
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            {status ? capitalizeFirst(status) : "-"}
          </span>
        )
      },
    },
    {
      name: <span className="jsr-col-title">Score</span>,
      width: "72px",
      center: true,
      cell: row => (
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          {row.secondary_evaluation?.score != null
            ? `${row.secondary_evaluation.score}/10`
            : "-"}
        </span>
      ),
    },
    {
      name: <span className="jsr-col-title">Scheduled</span>,
      selector: row => row.created_at,
      sortable: true,
      minWidth: "140px",
      cell: row => (
        <span title={formatDateTime(row.created_at)}>
          {formatDateTime(row.created_at)}
        </span>
      ),
    },
    {
      name: <span className="jsr-col-title">Action</span>,
      minWidth: "195px",
      cell: row => {
        const hasPrimary = row.primary_evaluation?.id
        const hasSecondary = row.secondary_evaluation?.id
        return (
          <div className="d-flex align-items-center flex-wrap gap-1">
            {hasPrimary && (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => handleViewFirstScreening(row)}
              >
                First Screening
              </Button>
            )}
            {hasSecondary && (
              <Button
                size="sm"
                variant="outline-success"
                onClick={() => handleViewSecondScreening(row)}
              >
                Screening Result
              </Button>
            )}
            {!hasPrimary && !hasSecondary && (
              <span className="text-muted">-</span>
            )}
          </div>
        )
      },
    },
  ]

  const talentScanColumns = [
    {
      name: <span className="jsr-col-title">Candidate</span>,
      selector: row => row.display_name || "-",
      sortable: true,
      minWidth: "190px",
      cell: row => {
        const talentScanId = getCandidateTalentScanId(row)
        const jId = getCandidateJobId(row)
        return (
          <AvatarCell
            name={row.display_name}
            email={row.display_email}
            isLink={!!talentScanId && !!jId}
            onClick={() => {
              if (talentScanId && jId) handleOpenTalentScanResults(row)
            }}
          />
        )
      },
    },
    {
      name: <span className="jsr-col-title">Phone</span>,
      selector: row => row.display_phone || "-",
      sortable: true,
      minWidth: "140px",
      cell: row => (
        <span title={row.display_phone || "-"}>{row.display_phone || "-"}</span>
      ),
    },
    {
      name: <span className="jsr-col-title">Telephonic Score</span>,
      selector: row => row.secondary_score ?? "-",
      sortable: true,
      minWidth: "150px",
      center: true,
      cell: row => {
        const score = row.secondary_score
        const hasScore = score !== null && score !== undefined && score !== "-"
        const recordCount = Array.isArray(row.score_records)
          ? row.score_records.length
          : 0
        if (!hasScore) {
          return (
            <span
              style={{
                cursor: recordCount ? "pointer" : "default",
                textDecoration: recordCount ? "underline" : "none",
              }}
              onClick={recordCount ? () => openScoreDetails(row) : undefined}
              title={recordCount ? "View attempt history" : undefined}
            >
              -
            </span>
          )
        }
        const numScore = Number(score)
        const displayScore = Number.isFinite(numScore)
          ? `${numScore.toFixed(1)}/10`
          : score
        return (
          <span
            // style={{ cursor: "pointer", textDecoration: "underline" }}
            // onClick={() => openScoreDetails(row)}
            title="View attempt history"
          >
            {displayScore}
          </span>
        )
      },
    },
    {
      name: <span className="jsr-col-title">Talent Scan Score</span>,
      selector: row => row.ts_percentage ?? "-",
      sortable: true,
      minWidth: "150px",
      center: true,
      cell: row => {
        const hasTsScore =
          row.ts_total_secured !== null && row.ts_total_secured !== undefined
        if (!hasTsScore) return <span>-</span>
        return (
          <span>
            {formatScore(row.ts_total_secured)} /{" "}
            {formatScore(row.ts_total_score)}
          </span>
        )
      },
    },
    {
      name: <span className="jsr-col-title">Action</span>,
      minWidth: "170px",
      cell: row => {
        const talentScanId = getCandidateTalentScanId(row)
        const jId = getCandidateJobId(row)
        const rowKey = row.job_candidate_id ?? row.candidate_id
        const isLoading = !!loadingResultMap[rowKey]
        return (
          <Button
            size="sm"
            variant="outline-primary"
            disabled={!talentScanId || !jId || isLoading}
            onClick={() => handleOpenTalentScanResults(row)}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                Loading...
              </>
            ) : (
              "View Result"
            )}
          </Button>
        )
      },
    },
  ]

  /* -------------------- header content per view -------------------- */
  const renderHeader = () => {
    if (view === "list") {
      return (
        <div className="jsr-hero">
          <button
            type="button"
            className="jsr-hero-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={15} />
          </button>
          <div className="jsr-hero-row">
            <div className="jsr-hero-icon">
              <PhoneCall size={16} />
            </div>
            <div>
              <div className="jsr-hero-title">
                Screening Results — {jobTitle}
              </div>
              <div className="jsr-hero-sub">
                Job ID: {jobId || "--"} &nbsp;·&nbsp; Telephonic & Talent Scan Results
              </div>
            </div>
          </div>
          <div className="jsr-tabs">
            <button
              type="button"
              className={`jsr-tab-btn ${
                activeTab === "telephonic" ? "active" : ""
              }`}
              onClick={() => handleTabChange("telephonic")}
            >
              <PhoneCall size={12} /> Telephonic Tests{" "}
              <span className="jsr-tab-count">{totalRows || 0}</span>
            </button>
            <button
              type="button"
              className={`jsr-tab-btn ${
                activeTab === "talentScan" ? "active" : ""
              }`}
              onClick={() => handleTabChange("talentScan")}
            >
              <FileBarChart2 size={12} /> Talent Scan{" "}
              <span className="jsr-tab-count">{talentScanTotalRows || 0}</span>
            </button>
          </div>
        </div>
      )
    }

    // Shared header shape for all detail views: back arrow + icon + title + subtitle.
    // NOTE: the close (X) button here intentionally calls backToList, not onClose —
    // closing out of a detail view (evaluation / talent scan / attempt history)
    // should return to the candidate list on whichever tab it was opened from,
    // not dismiss the whole modal. Only the list view's X fully closes the modal.
    let icon = <Phone size={16} />
    let title = ""
    let subtitle = ""
    if (view === "evaluation") {
      icon = <Phone size={16} />
      title = `${resultType} Result`
      subtitle = resultData
        ? `${capitalizeFirst(resultData.candidate_name)} · ${
            resultData.candidate_phone || "-"
          }`
        : "Loading candidate details…"
    } else if (view === "talentScan") {
      icon = <FileBarChart2 size={16} />
      title = "Talent Scan Result"
      const candidateName =
        talentScanResults?.[0]?.candidate_info?.name ||
        talentScanCandidate?.display_name ||
        "-"
      const candidateEmail =
        talentScanResults?.[0]?.candidate_info?.email ||
        talentScanCandidate?.display_email ||
        "-"
      const candidatePhone =
        talentScanResults?.[0]?.candidate_info?.phone ||
        talentScanCandidate?.display_phone ||
        "-"
      subtitle = `${candidateName} · ${candidateEmail} · ${candidatePhone}`
    } else if (view === "scoreDetails") {
      icon = <Clock size={16} />
      title = "Telephonic Attempt History"
      const c = scoreDetailsCandidate || {}
      subtitle = `${c.display_name || "-"} · ${c.display_email || "-"} · ${
        c.display_phone || "-"
      }`
    }

    return (
      <div className="jsr-hero">
        <button
          type="button"
          className="jsr-hero-close"
          onClick={backToList}
          aria-label="Close"
        >
          <X size={15} />
        </button>
        <div className="jsr-hero-row">
          <button
            type="button"
            className="jsr-back-btn"
            onClick={backToList}
            aria-label="Back to candidate list"
          >
            <ChevronLeft size={17} />
          </button>
          <div className="jsr-hero-icon">{icon}</div>
          <div style={{ minWidth: 0 }}>
            <div className="jsr-hero-title">{title}</div>
            <div
              className="jsr-hero-sub"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 520,
              }}
            >
              {subtitle}
            </div>
          </div>
        </div>
      </div>
    )
  }

  /* -------------------- list view content -------------------- */
  const renderListContent = () => (
    <>
      <div className="jsr-filter-panel">
        <div className="jsr-filter-bar">
          {activeTab === "telephonic" && (
            <div className="jsr-filter-group" style={{ width: 145 }}>
              <label>Status</label>
              <select
                className="form-select form-select-sm"
                value={screeningStatus}
                onChange={e => handleScreeningStatusChange(e.target.value)}
              >
                <option value="">All</option>
                {SCREENING_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="jsr-filter-group" style={{ width: 140 }}>
            <label>From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              max={toDate || undefined}
              onChange={e => handleFromDateChange(e.target.value)}
            />
          </div>
          <div className="jsr-filter-group" style={{ width: 140 }}>
            <label>To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              min={fromDate || undefined}
              onChange={e => handleToDateChange(e.target.value)}
            />
          </div>

          <div className="jsr-filter-group jsr-filter-search-wrap">
            <label>Search</label>
            <div style={{ position: "relative" }}>
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 9,
                  top: 9,
                  color: "#9ca3af",
                }}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ paddingLeft: 28, paddingRight: searchTerm ? 26 : 10 }}
                placeholder="Search candidate…"
                value={searchTerm}
                onChange={e => handleSearchChange(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => handleSearchChange("")}
                  style={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "none",
                    padding: 2,
                    cursor: "pointer",
                    color: "#9ca3af",
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="jsr-notes-inline">
            {activeTab === "telephonic" ? (
              <span
                className="jsr-note-chip"
                style={{
                  background: "linear-gradient(135deg,#fef3c7,#fde68a)",
                  color: "#92400e",
                  border: "1px solid #fcd34d",
                }}
              >
                <Clock size={11} /> Score updates 10–15 min after call
              </span>
            ) : (
              <>
                {screeningCutOff !== null && (
                  <span
                    className="jsr-note-chip"
                    style={{
                      background: "linear-gradient(135deg,#ede9fe,#ddd6fe)",
                      color: "#5b21b6",
                      border: "1px solid #c4b5fd",
                    }}
                  >
                    <Target size={11} /> Screening Cutoff :{" "}
                    {formatPct(screeningCutOff)}
                  </span>
                )}
                <span
                  className="jsr-note-chip"
                  style={{
                    background: "linear-gradient(135deg,#dbeafe,#bfdbfe)",
                    color: "#1e40af",
                    border: "1px solid #93c5fd",
                  }}
                >
                  <Clock size={11} /> Results in 15–20 min
                </span>
              </>
            )}
          </div>

          <Button
            size="sm"
            variant="outline-secondary"
            className="jsr-reset-btn"
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </div>
        {dateError && <div className="jsr-date-error">{dateError}</div>}
      </div>

      <div className="jsr-content-scroll">
        <div className="jsr-table-wrap">
          <div id="JSR_Table">
            {activeTab === "telephonic" ? (
              <DataTable
                key="jsr-telephonic"
                columns={columns}
                data={records}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationResetDefaultPage={resetPaginationToggle}
                paginationPerPage={perPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationComponentOptions={paginationComponentOptions}
                persistTableHead
                progressPending={loading}
                progressComponent={<CustomLoader />}
                noDataComponent={
                  <TableEmptyState
                    icon={<PhoneCall size={28} />}
                    title="No telephonic screening records found"
                  />
                }
              />
            ) : (
              <DataTable
                key="jsr-talentscan"
                columns={talentScanColumns}
                data={talentScanRecords}
                pagination
                paginationServer
                paginationTotalRows={talentScanTotalRows}
                paginationResetDefaultPage={resetPaginationToggle}
                paginationPerPage={talentScanPerPage}
                onChangePage={handleTalentScanPageChange}
                onChangeRowsPerPage={handleTalentScanPerRowsChange}
                paginationComponentOptions={paginationComponentOptions}
                persistTableHead
                progressPending={talentScanLoading}
                progressComponent={<CustomLoader />}
                noDataComponent={
                  <TableEmptyState
                    icon={<FileBarChart2 size={28} />}
                    title="No Talent Scan candidates found"
                    subtitle="Candidates who take the Talent Scan test for this job will appear here."
                  />
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  )

  /* -------------------- evaluation detail content -------------------- */
  const renderEvaluationContent = () => {
    if (resultLoading) {
      return (
        <div
          className="jsr-empty-state"
          style={{ animation: "jsrFadeIn 0.3s ease" }}
        >
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "jsrPulse 1.5s ease infinite",
              marginBottom: 12,
            }}
          >
            <Phone size={24} style={{ color: "#fff" }} />
          </div>
          <h6 className="text-muted mb-0">Loading screening results…</h6>
        </div>
      )
    }
    if (resultError) {
      return (
        <div className="jsr-empty-state">
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <Phone size={32} style={{ color: "#dc3545" }} />
          </div>
          <h5
            className="text-danger mb-0"
            style={{ fontWeight: 600, fontSize: 15 }}
          >
            {resultError}
          </h5>
        </div>
      )
    }
    if (!resultData) return null

    return (
      <div className="jsr-screening-split-body">
        <div className="jsr-screening-details-pane">
          <Row>
            <Col md={6}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 19,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {(resultData.candidate_name || "C")[0].toUpperCase()}
                </div>
                <div>
                  <h6
                    className="mb-0"
                    style={{ fontWeight: 700, fontSize: 14 }}
                  >
                    {capitalizeFirst(resultData.candidate_name)}
                  </h6>
                  <small className="text-muted" style={{ fontSize: 11.5 }}>
                    {capitalizeFirst(
                      resultData.designation || resultData.job_title
                    )}
                  </small>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #f093fb, #f5576c)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  <Bot size={21} />
                </div>
                <div>
                  <h6
                    className="mb-0"
                    style={{ fontWeight: 700, fontSize: 14 }}
                  >
                    {capitalizeFirst(resultData.agent_name)}
                  </h6>
                  <small style={{ fontSize: 11.5, color: "#f5576c" }}>
                    AI Recruiter
                  </small>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            {[
              {
                icon: <Phone size={14} />,
                label: "Phone",
                value: resultData.candidate_phone,
                color: "#0d6efd",
                bg: "#eff6ff",
              },
              {
                icon: <Briefcase size={14} />,
                label: "Designation",
                value: capitalizeFirst(resultData.job_title),
                color: "#059669",
                bg: "#ecfdf5",
              },
              {
                icon: <Calendar size={14} />,
                label: "Interview End Time",
                value:
                  resultData.date_of_interview &&
                  resultData.date_of_interview !== "-"
                    ? convertISTtoEST(resultData.date_of_interview)
                    : "-",
                color: "#7c3aed",
                bg: "#f5f3ff",
              },
              ...(resultType !== "Screening"
                ? [
                    {
                      icon: <Calendar size={14} />,
                      label: "Next Interview",
                      value:
                        resultData.next_interview &&
                        resultData.next_interview !== "-"
                          ? formatDateTime(resultData.next_interview)
                          : "-",
                      color: "#7c3aed",
                      bg: "#f5f3ff",
                    },
                  ]
                : []),
              ...(resultType === "Screening" &&
              resultData.overall_score !== null &&
              resultData.overall_score !== undefined
                ? [
                    {
                      icon: <Star size={14} />,
                      label: "Overall Score",
                      value: `${resultData.overall_score}/10`,
                      color: "#2563eb",
                      bg: "#eff6ff",
                      highlight: true,
                    },
                  ]
                : []),
            ].map((item, idx) => (
              <Col md={6} key={idx}>
                <div
                  className="d-flex align-items-center gap-2 mb-2 jsr-info-card"
                  style={{
                    background: item.highlight
                      ? "linear-gradient(135deg, #eff6ff, #dbeafe)"
                      : "#fff",
                    borderRadius: 9,
                    padding: "10px 12px",
                    border: item.highlight
                      ? "1px solid #93c5fd"
                      : "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      width: item.highlight ? 34 : 30,
                      height: item.highlight ? 34 : 30,
                      borderRadius: item.highlight ? "50%" : 7,
                      background: item.highlight
                        ? "linear-gradient(135deg, #3b82f6, #2563eb)"
                        : item.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: item.highlight ? "#fff" : item.color,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 9.5,
                        color: item.highlight ? "#2563eb" : "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: item.highlight ? 14 : 12.5,
                        fontWeight: 700,
                        color: item.highlight ? "#1e40af" : "#1f2937",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.value || "-"}
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {resultType === "Screening" && resultData.sections && (
            <div className="mb-3">
              <h6
                className="d-flex align-items-center gap-2 mb-2"
                style={{ fontWeight: 700, color: "#374151", fontSize: 13 }}
              >
                <div
                  style={{
                    width: 27,
                    height: 27,
                    borderRadius: 7,
                    background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Star size={14} style={{ color: "#d97706" }} />
                </div>
                Evaluation Sections
                <Badge
                  pill
                  bg="light"
                  text="dark"
                  style={{ fontSize: 9.5, fontWeight: 600 }}
                >
                  {Object.keys(resultData.sections).length}
                </Badge>
              </h6>
              <Row>
                {Object.entries(resultData.sections).map(([key, section]) => (
                  <Col md={12} key={key}>
                    <div
                      className="jsr-question-item"
                      style={{
                        background: "#fff",
                        borderRadius: 10,
                        padding: "12px 14px",
                        border: "1px solid #e5e7eb",
                        marginBottom: 8,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#1f2937",
                          }}
                        >
                          {section.section_name || key}
                        </span>
                        <Badge
                          pill
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            background:
                              section.score >= 8
                                ? "#059669"
                                : section.score >= 6
                                ? "#d97706"
                                : "#dc2626",
                            border: "none",
                          }}
                        >
                          {section.score}/10
                        </Badge>
                      </div>
                      <div
                        style={{
                          width: "100%",
                          height: 5,
                          borderRadius: 3,
                          background: "#e5e7eb",
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            width: `${(section.score / 10) * 100}%`,
                            height: "100%",
                            borderRadius: 3,
                            background:
                              section.score >= 8
                                ? "linear-gradient(90deg, #34d399, #059669)"
                                : section.score >= 6
                                ? "linear-gradient(90deg, #fbbf24, #d97706)"
                                : "linear-gradient(90deg, #f87171, #dc2626)",
                          }}
                        />
                      </div>
                      {section.feedback && (
                        <p
                          className="mb-1"
                          style={{
                            fontSize: 11.5,
                            color: "#4b5563",
                            lineHeight: 1.5,
                          }}
                        >
                          {section.feedback}
                        </p>
                      )}
                      {section.areas_of_improvement && (
                        <div
                          className="d-flex align-items-start gap-1 mt-1"
                          style={{ fontSize: 10.5, color: "#d97706" }}
                        >
                          <TrendingUp
                            size={12}
                            style={{ flexShrink: 0, marginTop: 2 }}
                          />
                          <span>{section.areas_of_improvement}</span>
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>

              {(resultData.key_strengths ||
                resultData.overall_recommendation ||
                resultData.priority_improvements) && (
                <Row className="mt-1">
                  {resultData.key_strengths && (
                    <Col md={4}>
                      <div
                        className="d-flex align-items-start gap-2 p-2 mb-2"
                        style={{
                          background: "#ecfdf5",
                          borderRadius: 9,
                          border: "1px solid #a7f3d0",
                          height: "calc(100% - 8px)",
                        }}
                      >
                        <Target
                          size={14}
                          style={{
                            color: "#059669",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#059669",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Key Strengths
                          </div>
                          <div style={{ fontSize: 12, color: "#065f46" }}>
                            {resultData.key_strengths}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                  {resultData.priority_improvements && (
                    <Col md={4}>
                      <div
                        className="d-flex align-items-start gap-2 p-2 mb-2"
                        style={{
                          background: "#fffbeb",
                          borderRadius: 9,
                          border: "1px solid #fde68a",
                          height: "calc(100% - 8px)",
                        }}
                      >
                        <AlertCircle
                          size={14}
                          style={{
                            color: "#d97706",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#d97706",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Priority Improvements
                          </div>
                          <div style={{ fontSize: 12, color: "#92400e" }}>
                            {resultData.priority_improvements}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                  {resultData.overall_recommendation && (
                    <Col md={4}>
                      <div
                        className="d-flex align-items-start gap-2 p-2 mb-2"
                        style={{
                          background: "#eff6ff",
                          borderRadius: 9,
                          border: "1px solid #bfdbfe",
                          height: "calc(100% - 8px)",
                        }}
                      >
                        <Sparkles
                          size={14}
                          style={{
                            color: "#2563eb",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#2563eb",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Recommendation
                          </div>
                          <div style={{ fontSize: 12, color: "#1e40af" }}>
                            {resultData.overall_recommendation}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              )}
            </div>
          )}

          {resultData.questions && resultData.questions.length > 0 && (
            <div className="mb-1">
              <h6
                className="d-flex align-items-center gap-2 mb-2"
                style={{ fontWeight: 700, color: "#374151", fontSize: 13 }}
              >
                <div
                  style={{
                    width: 27,
                    height: 27,
                    borderRadius: 7,
                    background: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ListChecks size={14} style={{ color: "#7c3aed" }} />
                </div>
                Screening Questions
                <Badge
                  pill
                  bg="light"
                  text="dark"
                  style={{ fontSize: 9.5, fontWeight: 600 }}
                >
                  {resultData.questions.length}
                </Badge>
              </h6>
              <Row>
                {resultData.questions.map((q, idx) => (
                  <Col md={6} key={idx}>
                    <div
                      className="d-flex align-items-start gap-3 jsr-question-item mb-2"
                      style={{
                        background: "#faf5ff",
                        borderRadius: 9,
                        padding: "10px 14px",
                        borderLeft: "3px solid #7c3aed",
                      }}
                    >
                      <span
                        style={{
                          background:
                            "linear-gradient(135deg, #7c3aed, #6d28d9)",
                          color: "#fff",
                          borderRadius: "50%",
                          width: 22,
                          height: 22,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10.5,
                          fontWeight: 700,
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#374151",
                          lineHeight: 1.5,
                        }}
                      >
                        {q}
                      </span>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>

        <div className="jsr-screening-transcript-pane">
          {resultData.recording && (
            <div className="mb-3" style={{ flexShrink: 0 }}>
              <h6
                className="d-flex align-items-center gap-2 mb-2"
                style={{ fontWeight: 700, color: "#374151", fontSize: 12.5 }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Volume2 size={13} style={{ color: "#d97706" }} />
                </div>
                Call Recording
              </h6>
              <div
                style={{
                  background: "linear-gradient(135deg, #fffbeb, #fef3c7)",
                  borderRadius: 11,
                  padding: "10px 12px",
                  border: "1px solid #fde68a",
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #f59e0b, #d97706)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Play size={14} style={{ color: "#fff" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <audio controls style={{ width: "100%", height: 30 }}>
                      <source src={resultData.recording} type="audio/wav" />
                    </audio>
                  </div>
                </div>
              </div>
            </div>
          )}

          {resultData.transcript && resultData.transcript.length > 0 ? (
            <div
              className="d-flex flex-column"
              style={{ flex: 1, minHeight: 0 }}
            >
              <h6
                className="d-flex align-items-center gap-2 mb-2"
                style={{
                  fontWeight: 700,
                  color: "#374151",
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 27,
                    height: 27,
                    borderRadius: 7,
                    background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MessageCircle size={14} style={{ color: "#2563eb" }} />
                </div>
                Conversation Transcript
                <Badge
                  pill
                  style={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    border: "none",
                  }}
                >
                  {resultData.transcript.length} messages
                </Badge>
              </h6>
              <div
                style={{
                  height: 520,
                  minHeight: 0,
                  overflowY: "auto",
                  borderRadius: 12,
                  padding: 14,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                }}
              >
                {resultData.transcript.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`d-flex mb-3 ${
                      msg.role === "ai"
                        ? "justify-content-start"
                        : "justify-content-end"
                    }`}
                  >
                    {msg.role === "ai" && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>AI Recruiter</Tooltip>}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #667eea, #764ba2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: 8,
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          <Bot size={15} style={{ color: "#fff" }} />
                        </div>
                      </OverlayTrigger>
                    )}
                    <div
                      style={{
                        maxWidth: "78%",
                        padding: "10px 14px",
                        borderRadius:
                          msg.role === "ai"
                            ? "4px 16px 16px 16px"
                            : "16px 4px 16px 16px",
                        background:
                          msg.role === "ai"
                            ? "#fff"
                            : "linear-gradient(135deg, #667eea, #764ba2)",
                        color: msg.role === "ai" ? "#1f2937" : "#fff",
                        fontSize: 12,
                        lineHeight: 1.6,
                        border:
                          msg.role === "ai" ? "1px solid #f0f0f0" : "none",
                      }}
                    >
                      <div
                        className="d-flex align-items-center gap-1 mb-1"
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          opacity: msg.role === "ai" ? 0.5 : 0.8,
                        }}
                      >
                        {msg.role === "ai" ? (
                          <Bot size={10} />
                        ) : (
                          <User size={10} />
                        )}
                        {msg.role === "ai" ? "AI Recruiter" : "Candidate"}
                      </div>
                      <p className="mb-0">{msg.content}</p>
                    </div>
                    {msg.role !== "ai" && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Candidate</Tooltip>}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #34d399, #059669)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginLeft: 8,
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          <User size={15} style={{ color: "#fff" }} />
                        </div>
                      </OverlayTrigger>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center"
              style={{ flex: 1, color: "#9ca3af", padding: "24px 0" }}
            >
              <MessageCircle
                size={28}
                style={{ marginBottom: 8, opacity: 0.5 }}
              />
              <small>No transcript available</small>
            </div>
          )}
        </div>
      </div>
    )
  }

  /* -------------------- talent scan detail content -------------------- */
  /* Matches the richer "Talent Scan Result" card design used on the
     Telephonic Tests page: header banner, 4-up metrics grid, candidate
     information + test timeline cards, and a section-scores table. */
  const renderTalentScanContent = () => {
    if (!talentScanResults.length) {
      return (
        <div className="jsr-empty-state">No Talent Scan result available.</div>
      )
    }

    const candidateName =
      talentScanResults?.[0]?.candidate_info?.name ||
      talentScanCandidate?.display_name ||
      "-"
    const candidateEmail =
      talentScanResults?.[0]?.candidate_info?.email ||
      talentScanCandidate?.display_email ||
      "-"
    const candidatePhone =
      talentScanResults?.[0]?.candidate_info?.phone ||
      talentScanCandidate?.display_phone ||
      "-"

    return (
      <div className="jsr-detail-body" style={{ background: "#f8fafc" }}>
        <div className="d-flex flex-column" style={{ gap: 14 }}>
          {talentScanResults.map((test, index) => {
            const testInfo = test?.test_info || {}
            const candidateInfo = test?.candidate_info || {}
            const atsDetails = test?.ats_details || {}
            const sectionScores = Array.isArray(test?.score_info)
              ? test.score_info
              : []
            const totalSecured =
              atsDetails.total_score_secured ?? test.total_candidate_score ?? 0
            const actualTotal =
              atsDetails.actual_total_score ?? test.total_test_score ?? 0
            const overallPct =
              atsDetails.percentage_score ??
              test.candidate_percentage_score ??
              0
            const qualified = !!atsDetails.is_test_qualified
            const detailKey = `${test?.id}-${test?.attempt_id}`
            const canViewResult = test?.id != null && test?.attempt_id != null
            const isCheated = !!testInfo.is_cheated
            const cheatedSection = testInfo.cheated_section || null
            const pctNumber = asNumber(overallPct)
            const scoreTone =
              pctNumber >= 70 ? "good" : pctNumber >= 50 ? "mid" : "low"
            const telephonicScore =
              talentScanCandidate?.secondary_score != null &&
              talentScanCandidate?.secondary_score !== "-"
                ? `${talentScanCandidate.secondary_score}/10`
                : "-"

            return (
              <div
                key={`${test?.id || index}-${test?.attempt_id || index}`}
                className="jsr-tsr-shell"
              >
                {/* Header banner */}
                <div className="jsr-tsr-header">
                  <div>
                    <div className="jsr-tsr-title">{textValue(testInfo.name)}</div>
                    <div className="jsr-tsr-subtitle">
                      {textValue(testInfo.test_designation)}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className={`jsr-tsr-badge ${
                        qualified ? "success" : "danger"
                      }`}
                    >
                      {qualified ? "Qualified" : "Not qualified"}
                    </span>
                    {isCheated && (
                      <span
                        className="jsr-tsr-badge"
                        style={{
                          background: "#fee2e2",
                          color: "#b91c1c",
                          border: "1px solid #fca5a5",
                        }}
                      >
                        <AlertCircle size={11} className="me-1" />
                        Cheating detected
                        {cheatedSection ? `: ${cheatedSection}` : ""}
                      </span>
                    )}
                  </div>
                </div>

                {/* Metrics grid: Secured/Actual, Overall %, Qualification, Telephonic score */}
                <div className="jsr-tsr-metrics">
                  <div className="jsr-tsr-metric info">
                    <div className="jsr-tsr-metric-val">
                      {formatScore(totalSecured)} / {formatScore(actualTotal)}
                    </div>
                    <div className="jsr-tsr-metric-label">Secured / Actual</div>
                  </div>
                  <div className={`jsr-tsr-metric ${scoreTone}`}>
                    <div className="jsr-tsr-metric-val">
                      {formatPct(overallPct)}%
                    </div>
                    <div className="jsr-tsr-metric-label">
                      Overall percentage
                    </div>
                  </div>
                  <div className={`jsr-tsr-metric ${qualified ? "good" : "low"}`}>
                    <div className="jsr-tsr-metric-val">
                      {qualified ? "Qualified" : "Not Qualified"}
                    </div>
                    <div className="jsr-tsr-metric-label">Qualification</div>
                  </div>
                  <div className="jsr-tsr-metric neutral">
                    <div className="jsr-tsr-metric-val">{telephonicScore}</div>
                    <div className="jsr-tsr-metric-label">
                      Telephonic score
                    </div>
                  </div>
                </div>

                {/* Candidate information + Test timeline */}
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <div className="jsr-tsr-info-card">
                      <div className="jsr-tsr-info-heading">
                        Candidate information
                      </div>
                      {[
                        {
                          label: "Name",
                          value: candidateInfo.name || candidateName,
                        },
                        {
                          label: "Email",
                          value: candidateInfo.email || candidateEmail,
                        },
                        {
                          label: "Phone",
                          value: candidateInfo.phone || candidatePhone,
                        },
                        {
                          label: "Experience",
                          value:
                            candidateInfo.experience !== null &&
                            candidateInfo.experience !== undefined
                              ? `${candidateInfo.experience} yrs`
                              : "-",
                        },
                      ].map(item => (
                        <div className="jsr-tsr-kv-row" key={item.label}>
                          <span className="jsr-tsr-kv-key">{item.label}</span>
                          <span className="jsr-tsr-kv-val">
                            {textValue(item.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="jsr-tsr-info-card">
                      <div className="jsr-tsr-info-heading">Test timeline</div>
                      {[
                        {
                          label: "Attempted",
                          value: formatTalentScanDate(test.attempted_at),
                        },
                        {
                          label: "Submitted",
                          value: formatTalentScanDate(test.submitted_at),
                        },
                        {
                          label: "Evaluated",
                          value: formatTalentScanDate(test.evaluated_at),
                        },
                        { label: "Attempt ID", value: test.attempt_id },
                      ].map(item => (
                        <div className="jsr-tsr-kv-row" key={item.label}>
                          <span className="jsr-tsr-kv-key">{item.label}</span>
                          <span className="jsr-tsr-kv-val">
                            {textValue(item.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>

                {/* Section scores table */}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle mb-0 jsr-ts-table">
                    <thead className="table-light">
                      <tr>
                        <th className="text-center">Section</th>
                        <th className="text-center">Questions</th>
                        <th className="text-center">Attempted</th>
                        <th className="text-center">Total</th>
                        <th className="text-center">Cutoff</th>
                        <th className="text-center">Secured</th>
                        <th className="text-center">Score %</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionScores.length ? (
                        sectionScores.map((section, idx) => {
                          const sectionPct = asNumber(section.percentage)
                          const sectionTone =
                            sectionPct >= 70
                              ? "good"
                              : sectionPct >= 50
                              ? "mid"
                              : "low"
                          const isCheatedRow =
                            isCheated &&
                            section.section__name === cheatedSection
                          return (
                            <tr
                              key={`${section.section__name}-${idx}`}
                              style={
                                isCheatedRow
                                  ? { background: "#fef2f2" }
                                  : undefined
                              }
                            >
                              <td
                                className="text-center"
                                style={{
                                  fontWeight: 700,
                                  color: isCheatedRow ? "#b91c1c" : undefined,
                                }}
                              >
                                {textValue(section.section__name)}
                              </td>
                              <td className="text-center">
                                {textValue(section.no_of_questions)}
                              </td>
                              <td className="text-center">
                                {textValue(section.no_of_questions_attempted)}
                              </td>
                              <td className="text-center">
                                {textValue(section.total_score)}
                              </td>
                              <td className="text-center">
                                {textValue(section.cutoff_score)}
                              </td>
                              <td className="text-center">
                                {textValue(section.score_secured)}
                              </td>
                              <td className="text-center">
                                <span
                                  className={`jsr-tsr-score-pill ${sectionTone}`}
                                >
                                  {formatPct(section.percentage)}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    section.is_qualified
                                      ? "bg-success"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {section.is_qualified
                                    ? "Qualified"
                                    : "Not qualified"}
                                </span>
                              </td>
                            </tr>
                          )
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center text-muted py-3"
                          >
                            No section scores found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {canViewResult && (
                  <div className="text-center mt-3">
                    <Button
                      size="sm"
                      className="blueBtn"
                      style={{ padding: "5px 16px", fontSize: 12 }}
                      disabled={!!viewingTestResultMap[detailKey]}
                      onClick={() => handleViewDetailedTalentResult(test)}
                    >
                      {viewingTestResultMap[detailKey]
                        ? "Opening..."
                        : "View Detailed Result"}
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  /* -------------------- attempt-history detail content -------------------- */
  const renderScoreDetailsContent = () => {
    const c = scoreDetailsCandidate || {}
    const historyRecords = Array.isArray(c.score_records)
      ? [...c.score_records]
          .filter(r => getScoreRecordStatus(r) === "Completed")
          .sort(
            (a, b) =>
              new Date(getScoreRecordDate(b) || 0) -
              new Date(getScoreRecordDate(a) || 0)
          )
      : []

    const statusStyle = status => {
      switch (status) {
        case "Completed":
          return { color: "#15803d", bg: "#dcfce7", border: "#a7f3d0" }
        case "Missed":
          return { color: "#dc2626", bg: "#fee2e2", border: "#fecaca" }
        case "Scheduled":
          return { color: "#d97706", bg: "#fef3c7", border: "#fde68a" }
        case "Sent":
          return { color: "#2563eb", bg: "#dbeafe", border: "#bfdbfe" }
        default:
          return { color: "#475467", bg: "#f1f5f9", border: "#e2e8f0" }
      }
    }
    const timelineSteps = record =>
      [
        { label: "Sent", value: record.sent_at },
        { label: "Scheduled", value: record.scheduled_at },
        { label: "Completed", value: record.completed_at },
        { label: "Missed", value: record.missed_at },
      ].filter(step => step.value)

    if (!historyRecords.length) {
      return (
        <div className="jsr-empty-state">
          No telephonic score records found.
        </div>
      )
    }

    return (
      <div className="jsr-detail-body" style={{ background: "#f8fafc" }}>
        <div className="d-flex flex-column" style={{ gap: 10 }}>
          {historyRecords.map((record, index) => {
            const status = getScoreRecordStatus(record)
            const s = statusStyle(status)
            const hasScore = record.score !== null && record.score !== undefined
            const scoreColor =
              hasScore && record.score >= 7
                ? "#15803d"
                : hasScore && record.score >= 5
                ? "#d97706"
                : "#dc2626"
            const scoreBg =
              hasScore && record.score >= 7
                ? "#dcfce7"
                : hasScore && record.score >= 5
                ? "#fef3c7"
                : "#fee2e2"
            const steps = timelineSteps(record)
            return (
              <div
                key={record.event_log_id || index}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  padding: "12px 14px",
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between flex-wrap mb-2"
                  style={{ gap: 8 }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 10 }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {historyRecords.length - index}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 12.5,
                          color: "#1f2937",
                        }}
                      >
                        {capitalizeFirst(record.event_type || "-")} attempt
                      </div>
                      <div style={{ fontSize: 10.5, color: "#9ca3af" }}>
                        Event ID #{textValue(record.event_log_id)}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span
                      className="badge"
                      style={{
                        background: s.bg,
                        color: s.color,
                        border: `1px solid ${s.border}`,
                        fontWeight: 700,
                      }}
                    >
                      {status}
                    </span>
                    <span
                      className="badge"
                      style={{
                        background: hasScore ? scoreBg : "#f1f5f9",
                        color: hasScore ? scoreColor : "#94a3b8",
                        fontWeight: 700,
                      }}
                    >
                      {hasScore
                        ? `${formatScore(record.score)}/10`
                        : "No score"}
                    </span>
                  </div>
                </div>
                {steps.length ? (
                  <div
                    className="d-flex flex-wrap"
                    style={{
                      gap: 8,
                      paddingTop: 8,
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    {steps.map(step => (
                      <div
                        key={step.label}
                        style={{
                          flex: "1 1 120px",
                          background: "#f8fafc",
                          borderRadius: 9,
                          padding: "6px 10px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9.5,
                            fontWeight: 700,
                            color: "#9ca3af",
                            textTransform: "uppercase",
                          }}
                        >
                          {step.label}
                        </div>
                        <div
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: "#374151",
                          }}
                        >
                          {formatTalentScanDate(step.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{ fontSize: 11.5, color: "#9ca3af", paddingTop: 6 }}
                  >
                    No timestamps recorded.
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  /* -------------------- render -------------------- */
  return (
    <>
      <style>{jsrStyles}</style>

      <Modal
        show={show}
        size="xl"
        centered
        className="jsr-modal"
        backdrop="static"
        onHide={() => {
          if (view === "list") {
            onClose() // close only from main list
          } else {
            backToList() // go back instead of closing
          }
        }}
      >
        <Modal.Body>
          <div className="jsr-shell">
            {renderHeader()}
            {view === "list" && renderListContent()}
            {view === "evaluation" && renderEvaluationContent()}
            {view === "talentScan" && (
              <div className="jsr-content-scroll">
                {renderTalentScanContent()}
              </div>
            )}
            {view === "scoreDetails" && (
              <div className="jsr-content-scroll">
                {renderScoreDetailsContent()}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{
            justifyContent: "center",
            borderTop: "1px solid #eef0f3",
            padding: "10px 20px",
          }}
        >
          <Button
            variant="secondary"
            className="btn btn-primary blueBtn"
            onClick={view === "list" ? onClose : backToList}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default JobScreeningResultsModal